/**
 * Data layer abstraction — routes between local IndexedDB (anonymous)
 * and Cloud Firestore (authenticated) transparently.
 *
 * Exports mirror db.ts signatures exactly so stores only need
 * an import path change.
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore';
import { db as firestoreDb } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';
import * as localDb from '@/lib/db';
import { ImportSchemas } from '@/lib/db';
import type {
  Recipe,
  CookingHistoryEntry,
  ShoppingListItem,
  PantryItem,
  MealPlanEntry,
  AppSettings,
  CookbookExport,
} from '@/types';
import { SCHEMA_VERSION } from '@/types';

/** Summary of what an import operation wrote or rejected. */
export interface ImportResult {
  recipesImported: number;
  historyImported: number;
  mealPlansImported: number;
  pantryImported: number;
  shoppingImported: number;
  settingsImported: boolean;
  skippedRecipes: number;
  skippedHistory: number;
  skippedPlans: number;
  skippedPantry: number;
  skippedShopping: number;
}

// Re-export constants
export { DEFAULT_SETTINGS, SETTINGS_VERSION, migrateSettings } from '@/lib/db';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getUid(): string | null {
  return useAuthStore.getState().user?.uid ?? null;
}

function userCol(uid: string, col: string) {
  return collection(firestoreDb, `users/${uid}/${col}`);
}

function userDoc(uid: string, col: string, id: string) {
  return doc(firestoreDb, `users/${uid}/${col}`, id);
}

/** Strip `undefined` values from an object (Firestore rejects them). */
function stripUndefined<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

export async function initDatabase(): Promise<void> {
  return localDb.initDatabase();
}

// ---------------------------------------------------------------------------
// Recipes
// ---------------------------------------------------------------------------

export async function getAllRecipes(): Promise<Recipe[]> {
  const uid = getUid();
  if (!uid) return localDb.getAllRecipes();

  const snap = await getDocs(userCol(uid, 'recipes'));
  return snap.docs.map((d) => d.data() as Recipe);
}

export async function getRecipe(id: string): Promise<Recipe | undefined> {
  const uid = getUid();
  if (!uid) return localDb.getRecipe(id);

  const snap = await getDoc(userDoc(uid, 'recipes', id));
  return snap.exists() ? (snap.data() as Recipe) : undefined;
}

export async function saveRecipe(recipe: Recipe): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.saveRecipe(recipe);

  await setDoc(userDoc(uid, 'recipes', recipe.id), stripUndefined(recipe));
}

export async function deleteRecipe(id: string): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.deleteRecipe(id);

  await deleteDoc(userDoc(uid, 'recipes', id));
}

export async function getRecipeCount(): Promise<number> {
  const uid = getUid();
  if (!uid) return localDb.getRecipeCount();

  const snap = await getDocs(userCol(uid, 'recipes'));
  return snap.size;
}

// ---------------------------------------------------------------------------
// Cooking History
// ---------------------------------------------------------------------------

export async function getCookingHistory(): Promise<CookingHistoryEntry[]> {
  const uid = getUid();
  if (!uid) return localDb.getCookingHistory();

  const snap = await getDocs(userCol(uid, 'cookingHistory'));
  return snap.docs
    .map((d) => d.data() as CookingHistoryEntry)
    .sort((a, b) => new Date(b.cookedAt).getTime() - new Date(a.cookedAt).getTime());
}

export async function addCookingHistoryEntry(entry: CookingHistoryEntry): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.addCookingHistoryEntry(entry);

  await setDoc(userDoc(uid, 'cookingHistory', entry.id), stripUndefined(entry));
}

export async function clearCookingHistory(): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.clearCookingHistory();

  const snap = await getDocs(userCol(uid, 'cookingHistory'));
  const batch = writeBatch(firestoreDb);
  snap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
}

// ---------------------------------------------------------------------------
// Shopping List
// ---------------------------------------------------------------------------

export async function getShoppingList(): Promise<ShoppingListItem[]> {
  const uid = getUid();
  if (!uid) return localDb.getShoppingList();

  const snap = await getDocs(userCol(uid, 'shoppingList'));
  return snap.docs.map((d) => d.data() as ShoppingListItem);
}

export async function saveShoppingListItem(item: ShoppingListItem): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.saveShoppingListItem(item);

  await setDoc(userDoc(uid, 'shoppingList', item.id), stripUndefined(item));
}

export async function deleteShoppingListItem(id: string): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.deleteShoppingListItem(id);

  await deleteDoc(userDoc(uid, 'shoppingList', id));
}

export async function clearShoppingList(): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.clearShoppingList();

  const snap = await getDocs(userCol(uid, 'shoppingList'));
  const batch = writeBatch(firestoreDb);
  snap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
}

// ---------------------------------------------------------------------------
// Pantry
// ---------------------------------------------------------------------------

export async function getPantryItems(): Promise<PantryItem[]> {
  const uid = getUid();
  if (!uid) return localDb.getPantryItems();

  const snap = await getDocs(userCol(uid, 'pantryItems'));
  return snap.docs
    .map((d) => d.data() as PantryItem)
    .sort((a, b) => a.ingredientName.localeCompare(b.ingredientName));
}

export async function savePantryItem(item: PantryItem): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.savePantryItem(item);

  await setDoc(userDoc(uid, 'pantryItems', item.id), stripUndefined(item));
}

export async function deletePantryItem(id: string): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.deletePantryItem(id);

  await deleteDoc(userDoc(uid, 'pantryItems', id));
}

export async function clearPantryItems(): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.clearPantryItems();

  const snap = await getDocs(userCol(uid, 'pantryItems'));
  const batch = writeBatch(firestoreDb);
  snap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
}

// ---------------------------------------------------------------------------
// Meal Plans
// ---------------------------------------------------------------------------

export async function getMealPlanEntries(): Promise<MealPlanEntry[]> {
  const uid = getUid();
  if (!uid) return localDb.getMealPlanEntries();

  const snap = await getDocs(userCol(uid, 'mealPlans'));
  return snap.docs
    .map((d) => d.data() as MealPlanEntry)
    .sort((a, b) => {
      const dateCmp = a.date.localeCompare(b.date);
      if (dateCmp !== 0) return dateCmp;
      return a.mealType.localeCompare(b.mealType);
    });
}

export async function saveMealPlanEntry(entry: MealPlanEntry): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.saveMealPlanEntry(entry);

  await setDoc(userDoc(uid, 'mealPlans', entry.id), stripUndefined(entry));
}

export async function deleteMealPlanEntry(id: string): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.deleteMealPlanEntry(id);

  await deleteDoc(userDoc(uid, 'mealPlans', id));
}

export async function clearMealPlanEntries(): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.clearMealPlanEntries();

  const snap = await getDocs(userCol(uid, 'mealPlans'));
  const batch = writeBatch(firestoreDb);
  snap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export async function getSettings(): Promise<AppSettings | undefined> {
  const uid = getUid();
  if (!uid) return localDb.getSettings();

  const snap = await getDoc(doc(firestoreDb, `users/${uid}/meta/settings`));
  return snap.exists() ? (snap.data() as AppSettings) : undefined;
}

export async function saveSettings(settings: AppSettings): Promise<void> {
  const uid = getUid();
  if (!uid) return localDb.saveSettings(settings);

  await setDoc(doc(firestoreDb, `users/${uid}/meta/settings`), stripUndefined(settings));
}

// ---------------------------------------------------------------------------
// Export / Import / Clear
// ---------------------------------------------------------------------------

export async function exportCookbook(): Promise<CookbookExport> {
  const [recipes, cookingHistory, shoppingList, pantryItems, mealPlans, settings] =
    await Promise.all([
      getAllRecipes(),
      getCookingHistory(),
      getShoppingList(),
      getPantryItems(),
      getMealPlanEntries(),
      getSettings(),
    ]);

  return {
    schemaVersion: SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    recipes,
    cookingHistory,
    shoppingList,
    pantryItems,
    mealPlans,
    settings: settings ?? localDb.DEFAULT_SETTINGS,
  };
}

/**
 * Import a cookbook backup. Validates every section with Zod schemas, then writes
 * through the data-layer save functions so authenticated users land in Firestore
 * (and unauthenticated users land in IndexedDB).
 *
 * Previous versions delegated to `localDb.importCookbook`, which wrote directly to
 * IndexedDB even when the user was signed in — meaning authenticated imports never
 * reached the cloud. This version runs identical validation but routes each write
 * through the shared save functions defined above.
 */
export async function importCookbook(data: CookbookExport): Promise<ImportResult> {
  if (!data.schemaVersion || data.schemaVersion > SCHEMA_VERSION) {
    throw new Error(
      `Incompatible schema version. Expected ${SCHEMA_VERSION} or lower, got ${data.schemaVersion ?? 'unknown'}`
    );
  }

  const result: ImportResult = {
    recipesImported: 0,
    historyImported: 0,
    mealPlansImported: 0,
    pantryImported: 0,
    shoppingImported: 0,
    settingsImported: false,
    skippedRecipes: 0,
    skippedHistory: 0,
    skippedPlans: 0,
    skippedPantry: 0,
    skippedShopping: 0,
  };

  // Recipes
  if (data.recipes && Array.isArray(data.recipes)) {
    for (const recipe of data.recipes) {
      const parsed = ImportSchemas.recipe.safeParse(recipe);
      if (parsed.success) {
        await saveRecipe({
          ...parsed.data,
          schemaVersion: SCHEMA_VERSION,
          updatedAt: new Date().toISOString(),
        } as Recipe);
        result.recipesImported++;
      } else {
        console.warn('Skipped invalid recipe during import:', recipe.name || 'unnamed', parsed.error.issues);
        result.skippedRecipes++;
      }
    }
  }

  // Cooking history
  if (data.cookingHistory && Array.isArray(data.cookingHistory)) {
    for (const entry of data.cookingHistory) {
      const parsed = ImportSchemas.cookingHistoryEntry.safeParse(entry);
      if (parsed.success) {
        await addCookingHistoryEntry(parsed.data as CookingHistoryEntry);
        result.historyImported++;
      } else {
        console.warn('Skipped invalid history entry during import:', entry.id || 'unnamed', parsed.error.issues);
        result.skippedHistory++;
      }
    }
  }

  // Meal plans
  if (data.mealPlans && Array.isArray(data.mealPlans)) {
    for (const entry of data.mealPlans) {
      const parsed = ImportSchemas.mealPlanEntry.safeParse(entry);
      if (parsed.success) {
        await saveMealPlanEntry(parsed.data as MealPlanEntry);
        result.mealPlansImported++;
      } else {
        console.warn('Skipped invalid meal plan during import:', entry.id || 'unnamed', parsed.error.issues);
        result.skippedPlans++;
      }
    }
  }

  // Pantry items
  if (data.pantryItems && Array.isArray(data.pantryItems)) {
    for (const item of data.pantryItems) {
      const parsed = ImportSchemas.pantryItem.safeParse(item);
      if (parsed.success) {
        await savePantryItem(parsed.data as PantryItem);
        result.pantryImported++;
      } else {
        console.warn('Skipped invalid pantry item during import:', item.id || 'unnamed', parsed.error.issues);
        result.skippedPantry++;
      }
    }
  }

  // Shopping list
  if (data.shoppingList && Array.isArray(data.shoppingList)) {
    for (const item of data.shoppingList) {
      const parsed = ImportSchemas.shoppingListItem.safeParse(item);
      if (parsed.success) {
        await saveShoppingListItem(parsed.data as ShoppingListItem);
        result.shoppingImported++;
      } else {
        console.warn('Skipped invalid shopping item during import:', item.id || 'unnamed', parsed.error.issues);
        result.skippedShopping++;
      }
    }
  }

  // Settings (merged with current)
  if (data.settings) {
    const currentSettings = (await getSettings()) ?? localDb.DEFAULT_SETTINGS;
    await saveSettings({
      ...localDb.DEFAULT_SETTINGS,
      ...currentSettings,
      ...data.settings,
    });
    result.settingsImported = true;
  }

  return result;
}

export async function clearAllData(): Promise<void> {
  const uid = getUid();

  // Always clear local
  await localDb.clearAllData();

  // Also clear cloud if authenticated
  if (uid) {
    const collections = ['recipes', 'cookingHistory', 'shoppingList', 'pantryItems', 'mealPlans'];
    for (const col of collections) {
      const snap = await getDocs(userCol(uid, col));
      if (snap.empty) continue;
      const batch = writeBatch(firestoreDb);
      snap.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
  }
}

export async function clearEverything(): Promise<void> {
  const uid = getUid();

  await localDb.clearEverything();

  if (uid) {
    const collections = ['recipes', 'cookingHistory', 'shoppingList', 'pantryItems', 'mealPlans'];
    for (const col of collections) {
      const snap = await getDocs(userCol(uid, col));
      if (snap.empty) continue;
      const batch = writeBatch(firestoreDb);
      snap.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
    // Delete settings doc
    await deleteDoc(doc(firestoreDb, `users/${uid}/meta/settings`));
  }
}
